import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Send, Sparkles, ShieldCheck, PanelRightOpen, FileSearch, Plus, Bot, User, AlertTriangle,
  History, CalendarClock, RefreshCcw, MessagesSquare, Download, Pencil, Trash2, Search, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { AppHeader } from "@/components/TopBar";
import { ModelSelector } from "@/components/ModelSelector";
import { LlmKeySettings } from "@/components/LlmKeySettings";
import { ModuleBadge, SectionTitle, ScoreBar, EmptyState, renderWithCitations } from "@/components/shared";
import { moduleMeta } from "@/lib/icons";
import { tenantApi, streamChat, LS } from "@/lib/apiClient";
import { cn } from "@/lib/utils";

const SUGGESTED = {
  governance_advisor: [
    "What are the first three AI commandments and their violation responses?",
    "Summarize the governing co-work charter rules.",
    "Who may modify the constitution core?",
  ],
  risk_compliance: [
    "What change-control procedure prevents deletion of records?",
    "What are the biggest control gaps or risks noted?",
    "Summarize the exceptions and holds in force.",
  ],
  value_analyst: [
    "What businesses are in the portfolio and who founded them?",
    "Summarize the value assessment dimensions and limitations.",
    "What is the competitive landscape?",
  ],
  ops_coordinator: [
    "What tasks are open on the team task board?",
    "Summarize recent AI-to-AI handoffs.",
    "Which AI employees are on shift?",
  ],
  chief_memory: [
    "Give an executive summary across governance, value and operations.",
    "What are the key system rules every AI must follow?",
    "What historical project snapshots are recorded?",
  ],
};

export default function TenantConsole() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [personaId, setPersonaId] = useState(localStorage.getItem(LS.persona) || "");
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState(localStorage.getItem(LS.chatModel) || "auto");
  const [resolvedModel, setResolvedModel] = useState("");
  const [resolvedProvider, setResolvedProvider] = useState("");
  const [keySource, setKeySource] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [currentSources, setCurrentSources] = useState([]);
  const [highlight, setHighlight] = useState(null);
  const [mobileSourcesOpen, setMobileSourcesOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSession, setLoadingSession] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [timelineYear, setTimelineYear] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [memQ, setMemQ] = useState("");
  const [memSearching, setMemSearching] = useState(false);
  const [memResults, setMemResults] = useState(null);
  const scrollRef = useRef(null);
  const sourcesRef = useRef({});

  const submitRename = useCallback(async () => {
    const title = renameValue.trim();
    if (!renameTarget || !title) return;
    try {
      await tenantApi.patch(`/tenant/sessions/${renameTarget.session_id}`, { title });
      setSessions((prev) => prev.map((s) =>
        s.session_id === renameTarget.session_id ? { ...s, title, custom_title: true } : s
      ));
      toast.success("Conversation renamed.");
      setRenameTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    }
  }, [renameTarget, renameValue]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await tenantApi.delete(`/tenant/sessions/${deleteTarget.session_id}`);
      setSessions((prev) => prev.filter((s) => s.session_id !== deleteTarget.session_id));
      if (sessionId === deleteTarget.session_id) {
        setMessages([]);
        setSessionId(null);
        setCurrentSources([]);
        setHighlight(null);
      }
      toast.success("Conversation deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget, sessionId]);

  const runMemorySearch = useCallback(async () => {
    const q = memQ.trim();
    if (!q) return;
    setMemSearching(true);
    try {
      const r = await tenantApi.get("/tenant/memory/search", { params: { q, k: 10 } });
      setMemResults(r.data.results || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally {
      setMemSearching(false);
    }
  }, [memQ]);

  const exportPdf = useCallback(async () => {
    if (!sessionId || exporting) return;
    setExporting(true);
    try {
      const r = await tenantApi.get(`/tenant/sessions/${sessionId}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `persistent-ai-brief-${sessionId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Conversation brief downloaded.");
    } catch (e) {
      toast.error("Could not export this conversation.");
    } finally {
      setExporting(false);
    }
  }, [sessionId, exporting]);

  const loadSessions = useCallback(async () => {
    try {
      const r = await tenantApi.get("/tenant/sessions");
      setSessions(r.data.sessions || []);
    } catch (e) {}
  }, []);

  const openSession = useCallback(async (sid) => {
    if (loadingSession) return;
    setLoadingSession(true);
    try {
      const r = await tenantApi.get(`/tenant/sessions/${sid}`);
      const msgs = (r.data.messages || []).map((m) => ({
        role: m.role,
        content: m.content,
        sources: m.sources || [],
        streaming: false,
      }));
      setMessages(msgs);
      setSessionId(sid);
      const lastAsst = [...(r.data.messages || [])].reverse().find((m) => m.role === "assistant");
      setCurrentSources(lastAsst?.sources || []);
      const lastPersona = [...(r.data.messages || [])].reverse().find((m) => m.persona_id)?.persona_id;
      if (lastPersona && personas.find((p) => p.id === lastPersona)) {
        setPersonaId(lastPersona);
        localStorage.setItem(LS.persona, lastPersona);
      }
      const lastModel = [...(r.data.messages || [])].reverse().find((m) => m.model_selection);
      if (lastModel?.model_selection && models.some((model) => model.id === lastModel.model_selection)) {
        setModelId(lastModel.model_selection);
        localStorage.setItem(LS.chatModel, lastModel.model_selection);
        setResolvedModel(lastModel.resolved_model || "");
        setResolvedProvider(lastModel.resolved_provider || "");
        setKeySource(lastModel.key_source || "");
      }
      setHighlight(null);
      toast.success("Conversation restored — continue where you left off.");
    } catch (e) {
      toast.error("Could not load that conversation.");
    } finally {
      setLoadingSession(false);
    }
  }, [loadingSession, personas, models]);

  const loadTimeline = useCallback(async () => {
    try {
      const r = await tenantApi.get("/tenant/timeline");
      setTimeline(r.data);
      const years = (r.data.years || []).map((y) => y.year).filter((y) => y > 0);
      setTimelineYear((prev) => (prev && years.includes(prev) ? prev : years[0] ?? 0));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Timeline unavailable");
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem(LS.tenantKey)) {
      navigate("/");
      return;
    }
    (async () => {
      try {
        const [meR, pR, modelR] = await Promise.all([
          tenantApi.get("/tenant/me"),
          tenantApi.get("/tenant/personas"),
          tenantApi.get("/tenant/models"),
        ]);
        setMe(meR.data);
        setPersonas(pR.data.personas);
        const stored = localStorage.getItem(LS.persona);
        const valid = pR.data.personas.find((p) => p.id === stored);
        setPersonaId(valid ? stored : pR.data.personas[0]?.id || "");
        const availableModels = modelR.data.models || [];
        setModels(availableModels);
        const storedModel = localStorage.getItem(LS.chatModel);
        const validModel = availableModels.find((model) => model.id === storedModel)?.id;
        const selectedModel = validModel || modelR.data.default || "auto";
        setModelId(selectedModel);
        localStorage.setItem(LS.chatModel, selectedModel);
      } catch (e) {
        toast.error("Session expired. Please sign in again.");
        localStorage.removeItem(LS.tenantKey);
        navigate("/");
      }
    })();
  }, [navigate]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const activePersona = personas.find((p) => p.id === personaId);

  const licensedModules = me?.licensed_modules || [];
  const allModules = me?.all_modules || [];

  const newChat = () => {
    setMessages([]);
    setSessionId(null);
    setCurrentSources([]);
    setHighlight(null);
    setResolvedModel("");
    setResolvedProvider("");
    setKeySource("");
  };

  const send = useCallback(
    async (text) => {
      const msg = (text ?? input).trim();
      if (!msg || sending) return;
      setInput("");
      setSending(true);
      setHighlight(null);
      const userMsg = { role: "user", content: msg };
      const asstMsg = { role: "assistant", content: "", sources: [], streaming: true };
      setMessages((prev) => [...prev, userMsg, asstMsg]);
      setCurrentSources([]);

      let localSources = [];
      await streamChat(
        { message: msg, persona_id: personaId, session_id: sessionId, job_id: "global", model: modelId },
        {
          onMeta: (ev) => {
            if (ev.session_id) setSessionId(ev.session_id);
            if (ev.resolved_model) setResolvedModel(ev.resolved_model);
            if (ev.resolved_provider) setResolvedProvider(ev.resolved_provider);
            if (ev.key_source) setKeySource(ev.key_source);
          },
          onSources: (srcs) => {
            localSources = srcs || [];
            setCurrentSources(srcs || []);
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = { ...copy[copy.length - 1], sources: srcs || [] };
              return copy;
            });
          },
          onToken: (tok) => {
            setMessages((prev) => {
              const copy = [...prev];
              const last = { ...copy[copy.length - 1] };
              last.content += tok;
              copy[copy.length - 1] = last;
              return copy;
            });
          },
          onError: (err) => {
            setMessages((prev) => {
              const copy = [...prev];
              const last = { ...copy[copy.length - 1] };
              last.content = last.content || "";
              last.error = err;
              last.streaming = false;
              copy[copy.length - 1] = last;
              return copy;
            });
            toast.error(err);
          },
          onDone: () => {
            setMessages((prev) => {
              const copy = [...prev];
              const last = { ...copy[copy.length - 1] };
              last.streaming = false;
              copy[copy.length - 1] = last;
              return copy;
            });
            setSending(false);
            loadSessions();
          },
        }
      );
    },
    [input, sending, personaId, sessionId, modelId, loadSessions]
  );

  const onCite = (citation) => {
    setHighlight(citation);
    setMobileSourcesOpen(true);
    const el = sourcesRef.current[citation];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => setHighlight(null), 1600);
  };

  const SourcesPanel = () => (
    <div className="flex flex-col h-full" data-testid="sources-panel">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <FileSearch className="h-4 w-4 text-primary" />
        <span className="font-display font-semibold text-sm">Evidence & Sources</span>
        {currentSources.length > 0 && (
          <span className="ml-auto text-xs text-muted-foreground">{currentSources.length} records</span>
        )}
      </div>
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-3">
          {currentSources.length === 0 ? (
            <div className="text-sm text-muted-foreground p-4 text-center">
              Sources retrieved for your question will appear here, each traceable to a governed
              record within your licensed modules.
            </div>
          ) : (
            currentSources.map((s, i) => {
              const meta = moduleMeta(s.module);
              const Icon = meta.Icon;
              const active = highlight === s.citation;
              return (
                <div
                  key={`${s.citation}-${i}`}
                  ref={(el) => (sourcesRef.current[s.citation] = el)}
                  data-testid={`evidence-card-${i}`}
                  className={cn(
                    "rounded-xl border p-3 transition-all",
                    active ? "border-primary ring-2 ring-primary/40 bg-primary/5" : "border-border bg-card"
                  )}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium", meta.color)}>
                      <Icon className="h-3.5 w-3.5" /> {s.module}
                    </span>
                    <ScoreBar score={s.score} />
                  </div>
                  <div className="font-mono text-[11px] text-muted-foreground mb-1.5">
                    {s.sheet} / {s.record_id}
                    {s.sensitive && (
                      <span className="ml-2 text-rose-300">restricted</span>
                    )}
                  </div>
                  <p className="text-[13px] text-foreground/90 leading-5 line-clamp-5">{s.snippet}</p>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <AppHeader
        right={
          me && (
            <div className="hidden sm:flex items-center gap-2 rounded-lg border border-border bg-secondary/50 px-3 py-1.5">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium" data-testid="tenant-name">{me.name}</span>
            </div>
          )
        }
      />

      <div className="flex-1 mx-auto w-full max-w-[1500px] px-2 sm:px-4 lg:px-6 py-4 grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)_380px] gap-4">
        {/* Left: persona + modules */}
        <aside className="hidden lg:flex flex-col gap-4">
          <Card className="p-4 bg-card border-border">
            <SectionTitle>GVI Persona</SectionTitle>
            <div className="mt-3">
              <Select
                value={personaId}
                onValueChange={(v) => {
                  setPersonaId(v);
                  localStorage.setItem(LS.persona, v);
                }}
              >
                <SelectTrigger data-testid="persona-switcher" className="bg-secondary/50">
                  <SelectValue placeholder="Select persona" />
                </SelectTrigger>
                <SelectContent>
                  {personas.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`persona-option-${p.id}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {activePersona && (
                <p className="mt-3 text-xs text-muted-foreground leading-5">{activePersona.description}</p>
              )}
            </div>
            <Separator className="my-4" />
            <Button variant="secondary" className="w-full" onClick={newChat} data-testid="new-chat-button">
              <Plus className="h-4 w-4" /> New Conversation
            </Button>
            {licensedModules.includes("social_memory") && (
              <Sheet open={timelineOpen} onOpenChange={(o) => { setTimelineOpen(o); if (o) loadTimeline(); }}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="w-full mt-2" data-testid="tenant-timeline-button">
                    <CalendarClock className="h-4 w-4" /> Memory Timeline
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[94vw] sm:w-[520px] p-0 bg-card overflow-hidden" data-testid="tenant-timeline-sheet">
                  <SheetHeader className="px-4 py-3 border-b border-border">
                    <SheetTitle className="flex items-center gap-2 text-sm font-display">
                      <CalendarClock className="h-4 w-4 text-primary" /> Your Memory Timeline
                      {timeline && (
                        <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary text-[11px]" data-testid="tenant-timeline-total-badge">
                          {timeline.total_memories.toLocaleString()} memories
                        </Badge>
                      )}
                    </SheetTitle>
                  </SheetHeader>
                  <div className="p-4 h-[calc(100vh-64px)] flex flex-col">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="relative flex-1">
                        <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <Input
                          value={memQ}
                          onChange={(e) => setMemQ(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") runMemorySearch(); }}
                          placeholder="Search your memories…"
                          data-testid="tenant-memory-search-input"
                          className="bg-secondary/40 pl-8 h-9"
                        />
                        {memResults !== null && (
                          <button
                            onClick={() => { setMemResults(null); setMemQ(""); }}
                            data-testid="tenant-memory-search-clear"
                            aria-label="Clear search"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <Button size="sm" onClick={runMemorySearch} disabled={memSearching} data-testid="tenant-memory-search-button">
                        {memSearching ? <RefreshCcw className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                    {memResults !== null ? (
                      <ScrollArea className="flex-1 pr-3">
                        <div className="space-y-2" data-testid="tenant-memory-search-results">
                          {memResults.length === 0 ? (
                            <div className="text-sm text-muted-foreground" data-testid="tenant-memory-search-empty">
                              No memories matched that query.
                            </div>
                          ) : (
                            memResults.map((r, i) => (
                              <div key={`${r.sheet}-${r.record_id}-${i}`} className="rounded-lg border border-border bg-secondary/20 p-3" data-testid={`tenant-memory-search-result-${i}`}>
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-mono text-[11px] text-blue-200">{r.sheet}/{r.record_id}</span>
                                  <span className="flex items-center gap-2 text-[11px] text-muted-foreground tabular-nums">
                                    {r.date && <span>{r.date}</span>}
                                    <span>match {(r.score * 100).toFixed(0)}%</span>
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground leading-5 mt-1.5">{r.snippet}</p>
                              </div>
                            ))
                          )}
                        </div>
                      </ScrollArea>
                    ) : !timeline ? (
                      <div className="space-y-2">
                        <div className="h-3 w-2/3 rounded shimmer-line" />
                        <div className="h-3 w-1/2 rounded shimmer-line" />
                      </div>
                    ) : timeline.total_memories === 0 ? (
                      <div className="text-sm text-muted-foreground" data-testid="tenant-timeline-empty">
                        No social memories indexed yet for your organization.
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 flex-wrap">
                          {(timeline.years || []).map((y) => (
                            <button
                              key={y.year}
                              onClick={() => setTimelineYear(y.year)}
                              data-testid={`tenant-timeline-year-${y.year}`}
                              className={cn(
                                "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                                timelineYear === y.year
                                  ? "border-primary/50 bg-primary/15 text-primary"
                                  : "border-border bg-secondary/30 text-muted-foreground hover:bg-secondary/60"
                              )}
                            >
                              {y.year === 0 ? "Undated" : y.year} <span className="tabular-nums opacity-70">({y.count})</span>
                            </button>
                          ))}
                        </div>
                        <ScrollArea className="flex-1 mt-3 pr-3">
                          <div className="space-y-2" data-testid="tenant-timeline-entries">
                            {((timeline.years || []).find((y) => y.year === timelineYear)?.entries || []).map((e, i) => (
                              <div key={`${e.sheet}-${e.record_id}-${i}`} className="rounded-lg border border-border bg-secondary/20 p-3">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-mono text-[11px] text-blue-200">{e.sheet}/{e.record_id}</span>
                                  {e.date && <span className="text-[11px] text-muted-foreground tabular-nums">{e.date}</span>}
                                </div>
                                <p className="text-xs text-muted-foreground leading-5 mt-1.5">{e.snippet}</p>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                        <p className="mt-2 text-[11px] text-muted-foreground flex items-center gap-1.5">
                          <ShieldCheck className="h-3 w-3 text-primary" /> Sanitized snippets only — exactly what your personas can see.
                        </p>
                      </>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            )}
          </Card>

          <Card className="p-4 bg-card border-border flex flex-col min-h-0">
            <div className="flex items-center justify-between">
              <SectionTitle>Conversations</SectionTitle>
              <button
                onClick={loadSessions}
                data-testid="session-history-refresh"
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Refresh conversations"
              >
                <RefreshCcw className="h-3.5 w-3.5" />
              </button>
            </div>
            <ScrollArea className="mt-3 max-h-64 -mx-1 px-1">
              <div className="space-y-1.5" data-testid="session-history-list">
                {sessions.length === 0 ? (
                  <div className="text-xs text-muted-foreground py-2" data-testid="session-history-empty">
                    Past conversations will appear here.
                  </div>
                ) : (
                  sessions.map((s, i) => (
                    <div
                      key={s.session_id}
                      className={cn(
                        "group relative w-full rounded-lg border transition-colors",
                        sessionId === s.session_id
                          ? "border-primary/40 bg-primary/10"
                          : "border-border bg-secondary/20 hover:bg-secondary/50"
                      )}
                    >
                      <button
                        onClick={() => openSession(s.session_id)}
                        data-testid={`session-item-${i}`}
                        className="w-full text-left px-2.5 py-2"
                      >
                        <div className="flex items-center gap-1.5 pr-10">
                          <MessagesSquare className="h-3 w-3 text-primary shrink-0" />
                          <span className="text-xs font-medium truncate">{s.title}</span>
                        </div>
                        <div className="mt-0.5 flex items-center justify-between text-[10px] text-muted-foreground">
                          <span className="inline-flex items-center gap-1"><History className="h-2.5 w-2.5" /> {formatTs(s.last_ts)}</span>
                          <span className="tabular-nums">{s.message_count} msgs</span>
                        </div>
                      </button>
                      <div className="absolute right-1.5 top-1.5 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => { e.stopPropagation(); setRenameTarget(s); setRenameValue(s.title); }}
                          data-testid={`session-rename-btn-${i}`}
                          aria-label="Rename conversation"
                          className="rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(s); }}
                          data-testid={`session-delete-btn-${i}`}
                          aria-label="Delete conversation"
                          className="rounded-md p-1 text-muted-foreground hover:text-rose-300 hover:bg-secondary transition-colors"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </Card>

          <Card className="p-4 bg-card border-border">
            <SectionTitle>Licensed Modules</SectionTitle>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="licensed-modules-badge-row">
              {allModules.map((m) => {
                const licensed = licensedModules.includes(m.key);
                return (
                  <ModuleBadge
                    key={m.key}
                    moduleKey={m.key}
                    name={m.name}
                    licensed={licensed}
                    testid={`module-badge-${m.key}`}
                  />
                );
              })}
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground leading-4">
              Retrieval is strictly limited to your licensed modules. Locked modules are never queried.
            </p>
          </Card>
        </aside>

        {/* Center: chat */}
        <main className="flex flex-col rounded-2xl border border-border bg-card/40 min-h-[70vh]">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-8 w-8 rounded-lg bg-primary/15 border border-primary/30 grid place-items-center shrink-0">
                <Bot className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="font-display font-semibold text-sm truncate">
                  {activePersona ? activePersona.name : "GVI Persona"}
                </div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {activePersona ? activePersona.title : "Grounded in governed memory"}
                </div>
              </div>
            </div>
            {/* Export + Mobile persona select + sources */}
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={exportPdf}
                disabled={!sessionId || exporting}
                data-testid="export-pdf-button"
                title={sessionId ? "Download this conversation as a PDF brief" : "Start a conversation first"}
              >
                {exporting ? <RefreshCcw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                <span className="hidden sm:inline">Export PDF</span>
              </Button>
              <div className="lg:hidden">
                <Sheet open={mobileSourcesOpen} onOpenChange={setMobileSourcesOpen}>
                  <SheetTrigger asChild>
                    <Button variant="secondary" size="sm" data-testid="mobile-sources-button">
                      <PanelRightOpen className="h-4 w-4" /> Sources
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[92vw] sm:w-[420px] p-0 bg-card">
                    <SourcesPanel />
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </div>

          <div className="px-4 py-2 border-b border-border space-y-2">
            <div className="lg:hidden">
              <Select value={personaId} onValueChange={(v) => { setPersonaId(v); localStorage.setItem(LS.persona, v); }}>
                <SelectTrigger data-testid="persona-switcher-mobile" className="bg-secondary/50 h-9 min-w-0">
                  <SelectValue placeholder="Persona" />
                </SelectTrigger>
                <SelectContent>
                  {personas.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
            <ModelSelector
              value={modelId}
              models={models}
              onChange={(value) => { setModelId(value); localStorage.setItem(LS.chatModel, value); }}
              disabled={sending || models.length === 0}
              testIdPrefix="chat-model"
              className="flex-1"
            />
              <LlmKeySettings disabled={sending} />
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-5" data-testid="chat-thread">
            {messages.length === 0 ? (
              <EmptyState
                icon={Sparkles}
                title={`Ask the ${activePersona?.name || "GVI persona"} anything`}
                subtitle="Answers are grounded strictly in your tenant's licensed governed memory, with citations to every source record."
                testid="chat-empty-state"
                action={
                  <div className="grid sm:grid-cols-1 gap-2 max-w-md">
                    {(SUGGESTED[personaId] || SUGGESTED.chief_memory).map((q, i) => (
                      <button
                        key={i}
                        onClick={() => send(q)}
                        data-testid={`suggested-prompt-${i}`}
                        className="text-left text-sm rounded-lg border border-border bg-secondary/40 hover:bg-secondary px-3 py-2.5 transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                }
              />
            ) : (
              messages.map((m, i) => <MessageBubble key={i} m={m} onCite={onCite} />)
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-border p-3">
            <div className="flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={`Message the ${activePersona?.name || "persona"}...`}
                data-testid="chat-composer-textarea"
                className="min-h-[52px] max-h-40 resize-none bg-secondary/40 border-border"
              />
              <Button
                onClick={() => send()}
                disabled={sending || !input.trim()}
                data-testid="chat-send-button"
                className="h-[52px] px-4"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground flex items-center gap-1.5">
              <ShieldCheck className="h-3 w-3 text-primary" />
              Grounded, citation-backed answers within your licensed modules only.
              {resolvedModel && (
                <span data-testid="resolved-chat-model">
                  · {resolvedProvider} / {resolvedModel}{keySource === "tenant" ? " · tenant key" : ""}
                </span>
              )}
            </div>
          </div>
        </main>

        {/* Right: sources (desktop) */}
        <aside className="hidden lg:block rounded-2xl border border-border bg-card/40 overflow-hidden">
          <SourcesPanel />
        </aside>
      </div>

      {/* Rename conversation dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => { if (!o) setRenameTarget(null); }}>
        <DialogContent className="bg-card border-border sm:max-w-md" data-testid="session-rename-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-base">Rename conversation</DialogTitle>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submitRename(); }}
            maxLength={80}
            placeholder="Conversation title"
            data-testid="session-rename-input"
            className="bg-secondary/40"
            autoFocus
          />
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRenameTarget(null)} data-testid="session-rename-cancel">Cancel</Button>
            <Button onClick={submitRename} disabled={!renameValue.trim()} data-testid="session-rename-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete conversation confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent className="bg-card border-border" data-testid="session-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-base">Delete this conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.title}" and its {deleteTarget?.message_count} messages will be permanently
              removed from your history. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="session-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="session-delete-confirm"
              className="bg-rose-600/90 hover:bg-rose-600 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function formatTs(ts) {
  try {
    const d = new Date(ts);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
      " " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function MessageBubble({ m, onCite }) {
  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="flex items-start gap-2 max-w-[85%]">
          <div className="rounded-2xl rounded-tr-sm bg-primary/15 border border-primary/25 px-4 py-2.5 text-sm leading-6">
            {m.content}
          </div>
          <div className="h-8 w-8 rounded-lg bg-secondary grid place-items-center shrink-0 mt-0.5">
            <User className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  }
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="flex justify-start">
      <div className="flex items-start gap-2 max-w-[90%]">
        <div className="h-8 w-8 rounded-lg bg-primary/15 border border-primary/30 grid place-items-center shrink-0 mt-0.5">
          <Bot className="h-4 w-4 text-primary" />
        </div>
        <div className="rounded-2xl rounded-tl-sm bg-secondary/50 border border-border px-4 py-3 text-sm leading-6 w-full">
          {m.error ? (
            <div className="flex items-center gap-2 text-rose-300">
              <AlertTriangle className="h-4 w-4" /> {m.error}
            </div>
          ) : m.content ? (
            <div className={cn("whitespace-pre-wrap break-words", m.streaming && "stream-caret")}>
              {renderWithCitations(m.content, onCite)}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="h-3 w-3/4 rounded shimmer-line" />
              <div className="h-3 w-1/2 rounded shimmer-line" />
              <div className="h-3 w-2/3 rounded shimmer-line" />
            </div>
          )}
          {!m.streaming && m.sources && m.sources.length > 0 && (
            <div className="mt-3 pt-2 border-t border-border/60 flex flex-wrap gap-1.5">
              {m.sources.slice(0, 6).map((s, i) => {
                const meta = moduleMeta(s.module);
                return (
                  <button
                    key={i}
                    onClick={() => onCite(s.citation)}
                    className={cn("rounded-md border px-1.5 py-0.5 font-mono text-[10px] transition-colors hover:brightness-125", meta.chip)}
                    data-testid={`msg-source-chip-${i}`}
                    title={s.citation}
                  >
                    {s.sheet}/{s.record_id}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
